import React, { FC, useContext, useMemo, useEffect } from 'react'
import { connect } from 'react-redux'
import { toast } from 'react-toastify'
import { Header, Modal, Segment } from 'semantic-ui-react'

import { CountryTypeEnum, IdentityEnum, PreChekInStepEnum } from '../../common/types/checkin'
import GlobalContext from '../../contexts/GlobalContext'
import { RootDispatch, RootState } from '../../store'
import ModalTransition from '../ModalTransition/ModalTransition'
import MomentCustomer from '../MomentCustomer/MomentCustomer'
import Identity from './form/Identity'
import PrincipalInfos from './form/PrincipalInfos'
import SecondaryInfos from './form/SecondaryInfos'
import PreCheckInStepHeader from './PreCheckInStepHeader';

// Steps
const Steps = {
  [PreChekInStepEnum.Identity]: {
    stepNumber: 1,
    Component: <Identity />
  },
  [PreChekInStepEnum.PrincipalInfos]: {
    stepNumber: 2,
    Component: <PrincipalInfos />
  },
  [PreChekInStepEnum.SecondaryInfos]: {
    stepNumber: 3,
    Component: <SecondaryInfos />
  }
}

const mapProps = (state: RootState) => ({
  bookings: state.booking.bookings,
  isBookerPax: state.booking.isBookerPax,
  modal: state.modal,
  checkin: state.checkin
})

const mapDispatch = (dispatch: RootDispatch) => ({
  handleCheckInData: dispatch.checkin.handleData,
  handleStepperForm: dispatch.checkin.handleStepperForm,
  resetCheckInData: dispatch.checkin.resetData,
  handleModal: dispatch.modal.handleModal
})

type Props = ReturnType<typeof mapProps> & ReturnType<typeof mapDispatch>

const ModalPreCheckIn: FC<Props> = ({
  bookings,
  isBookerPax,
  checkin,
  modal,
  handleModal,
  handleCheckInData,
  handleStepperForm,
  resetCheckInData
}) => {

  // Hooks
  const [globalContext] = useContext(GlobalContext) as any;
  const { user, establishment } = globalContext

  const booking = useMemo(() => {
    return bookings.find(booking => booking.id === checkin.selected.bookingId)
  }, [bookings, checkin])

  // Booker pax inside current booking
  const hasBookerPax = isBookerPax?.[booking?.id] 
  const roomingListId = checkin.selected.roomingListId

  const roomingList = useMemo(() => {
    if (!roomingListId || !booking?.roomingList) return {}
    return booking.roomingList.find(val => val.id === roomingListId)
  }, [booking, roomingListId])
  
  // Is current resident user
  const isIdentityResident = roomingList?.customerFileId === user.id
  // Set default values on modal open
  useEffect(() => {
    if (modal.preCheckInModal) {
      // it's a new pax if not found in roomingList and has not booker pax
 
      const newPax = (!hasBookerPax && !roomingList)

      const defaultValue = {
        // go to step 2 if hasBookerPax or in rooming list
        step: (hasBookerPax || roomingList) ? PreChekInStepEnum.PrincipalInfos : PreChekInStepEnum.Identity,
        // auto assign identity when it's booker pax or in rooming list because step 1 is passed
        identity: newPax ? null : isIdentityResident ? IdentityEnum.ResidentBase : IdentityEnum.RoomingList,
        // check user country
        countryType: user.country === establishment.country ? CountryTypeEnum.Resident : CountryTypeEnum.NonResident,
      }

      // assign default form parameters
      handleStepperForm(defaultValue)

      // assign default form data if not new pax
      if (!newPax) {
        isIdentityResident
          ? handleCheckInData({ [IdentityEnum.ResidentBase]: user })
          : handleCheckInData({ [IdentityEnum.RoomingList]: roomingList ?? {} })
      }
      
    } else {
      // reset preCheckIn data
      resetCheckInData()
    }

  }, [modal.preCheckInModal, hasBookerPax, roomingList, isIdentityResident])

  // Re-assign checkin data
  useEffect(() => {
    checkin.stepperForm.identity === IdentityEnum.ResidentBase
      ? handleCheckInData({ [IdentityEnum.ResidentBase]: user })
      : handleCheckInData({ [IdentityEnum.RoomingList]: roomingList ?? {} })
  }, [checkin.stepperForm.identity, user, roomingList])

  // Step change handler
  const handleStepGuard = (nextStep: PreChekInStepEnum) => {
    
    const { stepperForm } = checkin

    if (!stepperForm.identity) {
      toast.error(globalContext.translations.modalCheckInAlertStepModal)
      return
    }

    if (stepperForm.identity === IdentityEnum.ResidentBase) {
      stepperForm.countryType
        ? handleStepperForm({ step: nextStep })
        : toast.error(globalContext.translations.modalCheckInAlertStepModal) 
    }

    if (stepperForm.identity === IdentityEnum.RoomingList) {
      const nextStepNumber = Steps[nextStep].stepNumber
      nextStepNumber <= 2
        ? handleStepperForm({ step: nextStep })
        : toast.error(globalContext.translations.modalCheckInAlertStepModal)
    }
  }

  // Guards
  if (!booking) return null
  if (!Steps?.[checkin.stepperForm.step]) return null
  
  return (
    <div>
      <ModalTransition
        size="large"
        header={
          <Modal.Header>
            <Header>
              {globalContext.translations.modalCheckInBookingNumber} : {booking.incrementedResa} 
              <Header.Subheader>
                <div className="">{booking.nameRoom == undefined ? '':  `${globalContext.translations.chambre} : ${booking.nameRoom}`}</div>
                &nbsp;<strong className="mbBlue"><MomentCustomer format="L">{booking.arrivalDate}</MomentCustomer></strong> 
                &nbsp;{globalContext.translations.modalCheckInTo} 
                &nbsp;<strong style={{ color: "#2185d0" }}><MomentCustomer format="L">{booking.departureDate}</MomentCustomer></strong>
              </Header.Subheader>
            </Header>
          </Modal.Header>
        }
        content={
          <Modal.Description>
            <div id="formModal">
              {/** Steps header */}
              <Header>
                <PreCheckInStepHeader
                  stepperForm={checkin.stepperForm}
                  roomingList={roomingList}
                  isBookerPax={hasBookerPax}
                  handleStepGuard={handleStepGuard}
                />
              </Header>
              {/** Steps content */}
              <Segment style={{ width: "100%" }} attached>
                {Steps[checkin.stepperForm.step].Component}
              </Segment>
            </div>
          </Modal.Description>
        }
        open={modal.preCheckInModal}
        handleClose={() => handleModal({preCheckInModal: false})}
      />
    </div>
  )
}

export default connect(mapProps, mapDispatch)(ModalPreCheckIn)